import { PantryLocation } from '../utils/pantryLocation';

export type FoodCategory =
  | 'Fruits'
  | 'Vegetables'
  | 'Dairy'
  | 'Meat'
  | 'Beverages'
  | 'Condiments'
  | 'Spices'
  | 'Leftovers'
  | 'Packaged Foods'
  | 'Bread/Grains'
  | 'Plant-Based Protein'
  | 'Other';

export type QuantityEstimate = 'single' | 'multiple' | 'partial' | 'unknown' | 'dozen';

export type VisibleCondition = 'fresh' | 'good' | 'wilting' | 'near-expiry' | 'unknown' | 'slightly soft';

/** Legacy three-value vocabulary. Still read from Firestore and from model
 * responses issued before the prompts redeployed; never written any more. */
export type ShelfLifeEstimate = 'Use Soon' | '7d+' | 'Long-Term';

/**
 * How long something keeps, at the granularity a model can actually hold.
 *
 * Three buckets could not separate a whole chicken (two days) from a red onion
 * (six weeks) — both were "not produce" or "produce" and got one number each.
 * Five can. The service turns these into days; see shelfLifeService.
 */
export type ShelfLifeBucket =
  | 'highly-perishable'  // 1-3d   raw poultry, ground meat, fresh fish, leftovers, cut greens, berries
  | 'perishable'         // 4-7d   whole cuts, soft cheese, opened tofu, delicate produce
  | 'week-plus'          // 8-14d  sealed dairy, eggs, bread, cooked charcuterie, firm produce
  | 'keeps-weeks'        // 21-60d onions, garlic, potatoes, squash, hard cheese, sealed tofu, kombucha
  | 'shelf-stable';      //        sugar, syrup, cans, jars, spices, dry goods — no expiry pill

export const LEGACY_BUCKET_MAP: Record<ShelfLifeEstimate, ShelfLifeBucket> = {
  'Use Soon': 'perishable',
  '7d+': 'week-plus',
  'Long-Term': 'shelf-stable',
};

export const SHELF_LIFE_BUCKETS: ShelfLifeBucket[] = [
  'highly-perishable',
  'perishable',
  'week-plus',
  'keeps-weeks',
  'shelf-stable',
];

/** Buckets a cook should be nagged about. Replaces the old `=== 'Use Soon'` test. */
export const URGENT_BUCKETS: ShelfLifeBucket[] = ['highly-perishable', 'perishable'];

/**
 * The model's answer, or undefined.
 *
 * Every bucket in the app arrives as a string a model chose, and a model that
 * invents "very-perishable" must cost one item its hint, not write a row with a
 * null date that then reads as fresh forever.
 */
export function toShelfLifeBucket(raw: unknown): ShelfLifeBucket | undefined {
  if (typeof raw !== 'string') return undefined;
  const legacy = LEGACY_BUCKET_MAP[raw as ShelfLifeEstimate];
  if (legacy) return legacy;
  return SHELF_LIFE_BUCKETS.includes(raw as ShelfLifeBucket) ? (raw as ShelfLifeBucket) : undefined;
}

export type QualityRating = 'Poor' | 'Fair' | 'Good' | 'Excellent';

export interface BoundingBox {
  x: number; // Percentage from left (0-100)
  y: number; // Percentage from top (0-100)
  width: number; // Percentage width (0-100)
  height: number; // Percentage height (0-100)
}

export interface DetectedFoodItem {
  name: string;
  /** Both names, when the source gave both. See `InventoryItem.names`. */
  names?: { en: string; fr: string };
  category: FoodCategory;
  confidence: number;
  quantity_estimate: number | 'unknown'; // Phase 3: Now accepts numeric quantities or 'unknown'
  visible_condition: VisibleCondition;
  position?: BoundingBox; // Optional position data from AI

  // Unified shelf life estimation (both fridge and receipt modes). Legacy
  // three-value strings are still accepted on the way in and mapped through
  // LEGACY_BUCKET_MAP.
  shelf_life_estimate?: ShelfLifeBucket | ShelfLifeEstimate; // Fridge mode uses this
  expiry_category?: ShelfLifeBucket | ShelfLifeEstimate; // Receipt mode uses this (same vocabulary)
  /** Sealed or already opened, when the model can tell. Halves the estimate for most foods. */
  opened?: boolean;

  // Legacy expiry date format (kept for backward compatibility, but deprecated)
  expiry_date?: {
    days_until_expiry: number; // Number of days from today until expiry (0 = expires today, negative = expired)
    confidence: number; // 0-100
    explanation: string; // Reasoning for the estimate
  };

  // Pre-parsed quantity fields (from receipt scanner)
  quantity?: number; // Pre-parsed numeric quantity (e.g., 1.195 for "1.195 kg")
  unit?: string; // Pre-parsed unit (e.g., "kg", "g", "lb", "piece")

  // Receipt-specific fields (optional)
  unit_price?: number;
  total_price?: number;
  receipt_line?: string; // Original text from receipt

  /**
   * Where this food goes, when something upstream already knows.
   *
   * `FoodCategory` has no frozen member, so a receipt's "Frozen Foods" has to
   * collapse into `Packaged Foods` — a shelf-stable pantry category. The
   * storage signal therefore travels here instead of in the category, and the
   * pantry's keyword rules never get to overrule it. Also carries the capture
   * review's per-row choice and the photo mode's zone.
   */
  location?: PantryLocation;
}

/** A detected item as the review screens edit it. */
export interface EditableDetectedItem extends DetectedFoodItem {
  id: string;
  isAccepted: boolean;
  isEditing: boolean;
  isDeleted?: boolean;
  quantity?: number;
  unit?: string;
}

export interface AnalysisNotes {
  image_quality: QualityRating;
  lighting_conditions: QualityRating;
  total_items_detected: number;
  areas_analyzed: string[];
}

export interface ReceiptMetadata {
  store_name?: string;
  purchase_date?: string; // YYYY-MM-DD
  total_amount?: number;
  currency?: string;
  purchase_date_needs_review?: boolean; // true when the detected date was missing or failed validation
}

export interface FoodRecognitionResponse {
  detected_items: DetectedFoodItem[];
  analysis_notes: AnalysisNotes;
  receipt_metadata?: ReceiptMetadata; // Optional receipt-specific metadata
  /** Receipt mode only: deterministic post-parse cleanup log (dropped fee/discount lines, merged duplicates, sum-check). */
  validation_warnings?: string[];
  /** Receipt mode only: how well extracted item prices reconcile with the printed receipt total. */
  capture_confidence?: 'high' | 'medium' | 'low';
  /**
   * Tiled receipt scans only: how many of the image's bands were read
   * successfully, out of how many were attempted.
   *
   * `bands_ok < bands_total` means part of the receipt was never read. Bands
   * overlap by only 25%, so the middle ~50% of a lost band is covered by no
   * other band — the scan is genuinely incomplete and the user must be told
   * (issue #658 shipped 10 of 15 items with no warning at all).
   */
  bands_ok?: number;
  bands_total?: number;
  /**
   * Tiled receipt scans only: what each band read before the stitch, so a scan
   * the cook corrects can be replayed. Never rendered; it is sent to
   * `/api/receipt-scan` and dropped with the screen.
   */
  scan_evidence?: {
    band_reads: DetectedFoodItem[][];
    seam_merges: number;
    model?: string;
  };
}

export interface AIServiceError {
  message: string;
  code?: string;
  details?: any;
}